<style lang="scss">
.v-text-field > .v-input__control > .v-input__slot {
  transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.main-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

@mixin desktop-display {
  .main-container {
    height: 100vh;
  }
}

@media (min-width:800px) {
  @include desktop-display;
}

.barcode-container, .form-container {
  width: 600px;
  margin: 25px;

  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
}

.barcode-container {
  * {
    margin: 10px 0;
  }

  #reader, .scanner-prompt {
    margin: 0;
  }

  .scanner-prompt {
    display: flex;
    justify-content: center;
    align-items: center;

    .v-progress-circular {
      margin: 0 10px 0 0;
    }
  }
}

.form-container {
  .logo-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    h1 {
      margin: 25px 0;
    }
  }

  .desktop-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .v-image {
      margin-bottom: 25px;
    }
  }

  .v-form {
    display: flex;
    flex-direction: column;
  }

  .v-text-field {
    margin-top: 25px !important;
  }

  .password-field {
    margin-top: 5px !important;
  }

  .barcode-container {
    display: block;
  }

  .v-progress-circular {
    align-self: center !important;
  }

  .button-container {
    display: flex;
    flex-direction: column;
  }
}

.v-icon {
  margin-right: 5px;
}
</style>

<template>
  <div class="main-container">
    <div class="barcode-container" v-show="isScanning">
        <div v-show="isRequesting">
          <v-progress-circular
              v-show="true"
              indeterminate
              color="primary"
          ></v-progress-circular>
          <div>Requesting camera access...</div>
        </div>
      <div v-show="!isRequesting">
        <div v-show="hasCameraAccess">
          <div id="reader"></div>
          <div class="scanner-prompt">
            <v-progress-circular
                v-show="true"
                indeterminate
                color="primary"
                :size="25"
                :width="3"
            />
            <div>Attempting to find a barcode...</div>
          </div>
        </div>
        <div v-show="!hasCameraAccess">
          <v-icon>mdi-camera-lock</v-icon><br/>
          The page was unable to access your camera.<br/>
          Please confirm your browser is not blocking the request.
        </div>
      </div>
        <v-btn
            v-show="true"
            @click="this.stopScanning"
            color="primary"
            style="align-self:normal"
        ><v-icon>mdi-arrow-left</v-icon> Back</v-btn>
    </div>
    <div class="form-container" v-if="!isLoading && !isScanning">

      <div class="logo-container">
        <v-img :src="`${publicConfigBaseUrl}${config.libraryRoute}/banner.png`" />
        <h1>{{ this.config.title }}</h1>
      </div>

      <div
          v-show="!isMobile"
          class="desktop-container"
      >
        <vue-qr
            :margin="0"
            :logoMargin="2"
            :logoScale="0.2"
            :logoCornerRadius="0"
            :colorDark="this.config.qrCodeColor"
            :logoSrc="`${publicConfigBaseUrl}${config.libraryRoute}/qr.png`"
            :text="this.baseUrl + '/#/' + this.config.libraryRoute + (this.$route.query.barcode ? '?barcode=' + this.$route.query.barcode : '')"
            :size="250"
        />
        <br/>

        <div>{{this.config.qrPrompt}}</div>
      </div>

      <v-form
          v-show="isMobile"
          ref="form"
          v-model="isValid"
          v-on:submit.prevent="create"
          lazy-validation
      >
        <div>{{this.config.barcodePrompt}}</div>

        <v-text-field
            v-model="barcodeNumber"
            :rules="barcodeNumberRules"
            label="Barcode Number"
            outlined
            required
            append-icon="mdi-camera"
            @click:append="startScanning"
        />

        <v-text-field
            v-if="config.validatePassword"
            :rules="passwordRules"
            v-model="password"
            label="Password"
            type="password"
            outlined
            required
            class="password-field"
        />

        <v-alert
            class="text-left"
            :value="showError"
            outlined
            border="left"
            type="error"
        ><span v-html="errorMessage"></span></v-alert>

        <div class="button-container" v-show="!isBusy">
          <v-btn
              v-show="!isBusy"
              :disabled="!isValid"
              @click="create"
              color="primary"
          >
            Add to Wallet
          </v-btn>
        </div>

        <div v-show="isBusy">
          <v-progress-circular
              indeterminate
              color="primary"
          />
          <div>{{busyStatus}}</div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import {Html5Qrcode, Html5QrcodeScannerState} from "html5-qrcode"
import VueQr from 'vue-qr'

export default {
  /**
   * @typedef {Object} location
   * @property {string} text
   * @property {string} latitude
   * @property {string} longitude
   */

  /**
   * @typedef {Object} siteTheme
   * @property {string} buttonColor
   * @property {string} qrCodeColor
   */

  /**
   * @typedef {Object} appleCardTheme
   * @property {string} cardBackgroundColor
   * @property {string} cardLabelColor
   * @property {string} cardForegroundColor
   * @property {string} associatedAppStoreId
   */

  /**
   * @typedef {Object} androidCardTheme
   * @property {string} cardBackgroundColor
   * @property {string} associatedUrlLabel
   * @property {string} associatedUrl
   */

  /**
   * @typedef {Object} themes
   * @property {siteTheme} siteTheme
   * @property {appleCardTheme} appleCardTheme
   * @property {androidCardTheme} androidCardTheme
   */

  /**
   * @typedef {Object} config
   * @property {string} title
   * @property {string} qrPrompt
   * @property {string} barcodePrompt
   * @property {string} issuerName
   * @property {string} cardName
   * @property {boolean} isNfc
   * @property {number} libraryId
   * @property {boolean} validateBarcode
   * @property {boolean} validatePassword
   * @property {Location[]} locations
   * @property {themes} themes
   */

  name: 'HelloWorld',

  data: () => ({
    // publicConfigBaseUrl: 'assets/',
    publicConfigBaseUrl: 'https://stcardgeneratorau.blob.core.windows.net/public-config/',

    config: {
      title: '',
      qrPrompt: '',
      barcodePrompt: '',
      libraryRoute: '',
      qrCodeColor: '',
    },

    isMobile: false,
    isLoading: true,
    isScanning: false,
    isRequesting: true,
    hasCameraAccess: false,
    isValid: true,
    isBusy: false,
    busyStatus: '',

    showError: false,
    errorMessage: '',

    baseUrl: window.location.origin,
    platform: 'unknown',
    barcodeNumber: '',
    barcodeNumberRules: [
      v => !!v || 'Barcode Number is required'
    ],

    barcodeScanner: null,

    password: '',
    passwordRules: [
      v => !!v || 'Password is required'
    ]
  }),

  async mounted() {
    if (navigator.platform.indexOf('Win') !== -1 ||
        navigator.platform.indexOf('Mac') !== -1 ||
        navigator.platform.indexOf('X11') !== -1)
      this.platform = 'desktop';

    if (navigator.platform.indexOf('iPhone') !== -1 ||
        navigator.platform.indexOf('iPad') !== -1 ||
        navigator.platform.indexOf('iPod') !== -1)
    {
      this.isMobile = true;
      this.platform = 'ios';
    }

    if (navigator.platform.indexOf('Android') !== -1 ||
        navigator.platform.indexOf('Linux') !== -1) {
      this.isMobile = true;
      this.platform = 'android';
    }

    let pageData = this;
    let libraryRoute = this.$route.params.library;
    fetch(`${this.publicConfigBaseUrl}${libraryRoute}/config.json`)
        .then(response => response.json(), reason => Promise.reject(reason))
        .then(
            /**
             * @param {config} json
             */
            json => {
              document.title = json.title;
              pageData.config = json;
              pageData.config.libraryRoute = libraryRoute;
              pageData.config.qrCodeColor = json.themes.siteTheme.qrCodeColor;
              pageData.$vuetify.theme.themes.light.primary = json.themes.siteTheme.buttonColor;
              this.barcodeScanner = new Html5Qrcode("reader");
              pageData.isLoading = false;

              if (this.$route.query.barcode) {
                this.barcodeNumber = this.$route.query.barcode;

                if (!json.validatePassword && this.isMobile)
                  this.createPatronCard(this.platform, this.barcodeNumber, libraryRoute);
              }
            },
            () => {
              this.redirectTo404();
            }
        );
  },

  methods: {
    redirectTo404() {
      let enteredUrl = window.location.href;
      this.$router.push('/').then(() => history.replaceState(null, "", enteredUrl));
    },

    create() {
      if (!this.$refs.form.validate())
        return;

      this.createPatronCard(this.platform, this.barcodeNumber, this.config.libraryRoute);
    },

    async createPatronCard(platform, barcodeNumber, libraryRoute) {
      try {
        this.isBusy = true;
        this.showError = false;

        let apiAddress = 'https://api.fetechgroup.com/card-generator';

        if (process.env.NODE_ENV === 'development') {
          // Use locally hosted function app during development
          let host = window.location.host.split(':')[0];
          apiAddress = `http://${host}:7071/api`;
        }

        this.busyStatus = "Creating Library Card..."
        console.log(`Creating ${platform} card for ${barcodeNumber}`);

        let response = await fetch(`${apiAddress}/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            platform: platform,
            library: libraryRoute,
            barcodeNumber: barcodeNumber,
            password: this.password,
            patronName: null
          }),
        });

        console.log('Response:', response);

        if (response.ok) {
          // For android redirect to URL returned in response body
          if (platform === 'android') {
            let googlePayUrl = await response.text();
            this.busyStatus = "Adding card to Google Pay..."
            console.log(`Redirecting to Google Pay: ${googlePayUrl}`);

            // This sometimes isn't opening the Google Pay app but a 302 redirect from the server will?
            // Need to investigate a better way to deep link
            window.location.href = googlePayUrl;
            setTimeout(() => this.isBusy = false, 10000);
            
          } else if (platform === 'ios') {
            this.busyStatus = "Adding card to Apple Wallet..."
            console.log(`Downloading Apple Wallet pass...`);

            // Create pkpass download object from response bytes
            let pkPassBytes = await response.arrayBuffer();

            let blob = new Blob([pkPassBytes], {type: "application/vnd.apple.pkpass"});
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'pass.pkpass';
            link.click();

            this.isBusy = false;
          }

          // For ios download pkpass object returned in response body

        } else {
          // Failures could be due to invalid barcode number or failing to load config on server
          // Common errors with clean messages will return status 400 (bad request)
          let error = await response.text()
          let isUnexpected = response.status !== 400;
          if (isUnexpected)
            error = `${response.status}: ${error}`

          this.isBusy = false;
          this.setErrorMessage(error, isUnexpected);
        }
      }
      catch (exception) {
        this.isBusy = false;
        this.setErrorMessage(exception.message, true);
      }
    },

    setErrorMessage(error, isUnexpected) {
      let subMessage = '';
      if (isUnexpected || !error) {
        subMessage = error;
        error = "An unexpected error occurred while creating your Library Card. Please contact Library Staff for assistance.";
      }

      console.warn(`Failed to generate card: ${error} ${subMessage}`);
      this.errorMessage = `<div>${error}</div>`;
      if (subMessage)
        this.errorMessage += `<div class="mt-2 caption">${subMessage}</div>`;
      this.showError = true;
    },

    startScanning() {
      this.isScanning = true;
      this.isRequesting = true;
      this.hasCameraAccess = false;

      Html5Qrcode.getCameras().then(devices => {
        if (devices && devices.length) {
          this.isRequesting = false;
          this.hasCameraAccess = true;

          this.barcodeScanner.start({ facingMode: "environment" }, {}, (decodedText) => {
            this.stopScanning();
            this.barcodeNumber = decodedText;
          });
        }
      }).catch(err => {
        console.warn(err);
        this.isRequesting = false;
        this.hasCameraAccess = false;
      })
    },

    stopScanning() {
      if (this.barcodeScanner.getState() === Html5QrcodeScannerState.SCANNING) this.barcodeScanner.stop();
      this.isScanning = false;
      this.isRequesting = false;
    },
  },

  components: {
    VueQr
  }
}

</script>
